import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useStores } from "@stores";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import { ReactComponent as CloseIcon } from "@assets/icons/close.svg";
import {
  getPublishedBanners,
  PublicBanner,
  SEVERITY_STYLES,
} from "@services/notificationBannerService";
import usePolling from "@src/hooks/usePolling";

const DISMISS_KEY = "notif_banner_dismissed";
// Banners are edited by hand a few times a month, so a slow poll is plenty.
// Via usePolling (visibility-aware): a backgrounded tab stops polling entirely
// and refreshes the moment it's focused again. Both matter for cost — every
// poll is an authenticated request, and a steady drip of them keeps the Neon
// compute from ever reaching the 5 min of silence it needs to scale to zero.
const POLL_INTERVAL_MS = 600_000;

/** Screens use a hard `height: 100vh` Layout that reads this var, so the banner
 * stack must publish its total height. Defaults to 0px when empty → no layout change. */
const BANNER_HEIGHT_VAR = "--banner-height";
const setBannerHeightVar = (px: number) =>
  document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, `${px}px`);

/** A dismissal is keyed on id + updatedAt, so editing a banner re-shows it. */
const bannerKey = (b: PublicBanner) => `${b.id}:${b.updatedAt ?? ""}`;

const readDismissed = (): Set<string> => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};

const writeDismissed = (keys: Set<string>) => {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify([...keys]));
  } catch {
    /* ignore */
  }
};

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Bar = styled.div<{ bg: string; color: string }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  padding: 10px 48px;
  background: ${({ bg }) => bg};
  color: ${({ color }) => color};
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-align: center;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 8px 40px;
    font-size: 13px;
  }
`;

const CloseButton = styled.button<{ color: string }>`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${({ color }) => color};

  svg {
    width: 18px;
    height: 18px;
  }
  svg path {
    stroke: ${({ color }) => color};
    fill: ${({ color }) => color};
  }
`;

const NotificationBanner: React.FC = () => {
  const { accountStore } = useStores();
  const [banners, setBanners] = useState<PublicBanner[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(() => readDismissed());
  const stackRef = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await getPublishedBanners();
      setBanners(data);
      // Prune dismissed keys that no longer correspond to a live banner.
      const live = new Set(data.map(bannerKey));
      setDismissed((prev) => {
        const next = new Set([...prev].filter((k) => live.has(k)));
        if (next.size !== prev.size) writeDismissed(next);
        return next;
      });
    } catch {
      // network/auth hiccup — leave current state as-is
    }
  }, []);

  useEffect(() => {
    if (!accountStore.authenticated) {
      setBanners([]);
      return;
    }
    refresh();
  }, [accountStore.authenticated, refresh]);

  // Pause while the tab is hidden; refresh immediately when it regains focus.
  usePolling(refresh, POLL_INTERVAL_MS, accountStore.authenticated);

  const handleDismiss = (b: PublicBanner) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(bannerKey(b));
      writeDismissed(next);
      return next;
    });
  };

  const visible = accountStore.authenticated
    ? banners.filter((b) => b.message.trim() && !dismissed.has(bannerKey(b)))
    : [];

  // Keep `--banner-height` in sync with the stack's total rendered height
  // (banners wrap on mobile; the stack grows/shrinks as they're dismissed).
  useLayoutEffect(() => {
    if (visible.length === 0 || !stackRef.current) {
      setBannerHeightVar(0);
      return;
    }
    const el = stackRef.current;
    const update = () => setBannerHeightVar(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      setBannerHeightVar(0);
    };
  }, [visible.length]);

  if (visible.length === 0) {
    return null;
  }

  return (
    <Stack ref={stackRef}>
      {visible.map((b) => {
        const style = SEVERITY_STYLES[b.severity] ?? SEVERITY_STYLES.info;
        return (
          <Bar key={b.id} bg={style.bg} color={style.text}>
            <span>{b.message}</span>
            <CloseButton
              color={style.text}
              onClick={() => handleDismiss(b)}
              aria-label="Dismiss"
            >
              <CloseIcon />
            </CloseButton>
          </Bar>
        );
      })}
    </Stack>
  );
};

export default observer(NotificationBanner);
